import { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useWorkflowActions } from '../stores/useWorkflowStore';
import useWorkflowStore from '../stores/useWorkflowStore';
import useEditorStore from '../stores/useEditorStore';
import useAxesAlignmentStore from '../stores/useAxesAlignmentStore';
import { useShallow } from 'zustand/shallow';
import {
  PATHS,
  SCAN_PROCESS_STEPS,
  WORKFLOW_TYPES,
  MAIN_PROCESS_STEPS,
  GLOBAL_STEP_ORDER,
} from '../constants/flowConfig';
import { loadBkDesignOrderContext } from '../lib/bkDesignOrderContext';
import { uploadStepCheckpoint } from '../lib/stepCheckpointUpload';
import { CHECKPOINT_STEPS } from '../constants/stepCheckpointSlots';
import { getBkErrorMessage, uploadFinalStl } from '../services/erpApiService';
import { exportMesh } from '../lib/exporters/modelExporter';
import { EXPORT_FORMATS } from '../lib/exportConfig';
import { yieldForPaint } from '../lib/yieldToMain';
import {
  getBackwardDiscardMessage,
  isBackwardStepNavigation,
  resolvePreviousStep,
} from '../lib/stepNavigationUtils';
import { revertToStep, setPendingRevertNavigation } from '../../features/step-checkpoints';
import {
  runPostExportPrune,
  clampBackwardTarget,
  stageRewindMessage,
} from '../../features/step-checkpoints/undo/postExport';
import useBackwardNavigationStore from '../stores/useBackwardNavigationStore';
import useSkipStepStore from '../stores/useSkipStepStore';
import useLockingSystemStore from '../stores/useLockingSystemStore';
import useOutwardThicknessStore from '../stores/useOutwardThicknessStore';
import useTrimlineStore from '../stores/useTrimlineStore';
import {
  getSkipDialogPlan,
  stepHasUserChanges,
  stepRequiresApply,
} from '../lib/stepToolStatus';
import { isStepSkipOverridden } from '../lib/stepSkipOverrides';
import { notifyBkFinalExportSuccessUi } from '../lib/bkFinalExportCreditsUi';

const ORDERS_REDIRECT_URL = 'https://dev.benx.bengaitlabs.com/orders';

export function useStepNavigation() {
  const navigate = useNavigate();
  const [isCheckpointUploading, setIsCheckpointUploading] = useState(false);
  const pendingBackwardNav = useBackwardNavigationStore((state) => state.pending);
  const setPendingBackwardNav = useBackwardNavigationStore((state) => state.setPending);
  const clearPendingBackwardNav = useBackwardNavigationStore((state) => state.clearPending);
  const pendingSkip = useSkipStepStore((state) => state.pending);
  const setPendingSkip = useSkipStepStore((state) => state.setPending);
  const clearPendingSkip = useSkipStepStore((state) => state.clearPending);

  // Reactive apply-state for the steps that cannot be skipped, so the Next
  // button re-enables the moment a tool is applied.
  const isThicknessApplied = useOutwardThicknessStore((state) => state.isApplied);
  const isTrimlineApplied = useTrimlineStore(
    (state) => Boolean(state.isTrimlineApplied) || Boolean(state.applied),
  );
  const isCheckpointUploadingRef = useRef(false);
  const isRevertingRef = useRef(false);
  const currentStep = useWorkflowStore((state) => state.currentStep);
  const stepCompletion = useWorkflowStore((state) => state.stepCompletion);
  const { completeCurrentStep, setCurrentWorkflow, setCurrentStep, setStepCompletion } = useWorkflowActions();
  const clearToolStates = useEditorStore((state) => state.clearToolStates);

  const [areAllSelectionsCompleted, isAligned] = useAxesAlignmentStore(
    useShallow((state) => [state.areAllSelectionsCompleted, state.isAligned])
  );

  const beginCheckpointUpload = async () => {
    if (isCheckpointUploadingRef.current) return false;
    isCheckpointUploadingRef.current = true;
    setIsCheckpointUploading(true);
    await yieldForPaint();
    return true;
  };

  const endCheckpointUpload = () => {
    isCheckpointUploadingRef.current = false;
    setIsCheckpointUploading(false);
  };

  /**
   * Fire-and-forget a checkpoint upload so the user can move to the next step
   * immediately instead of waiting for the network round-trip. Failures surface
   * as a toast but never block navigation.
   */
  const runCheckpointInBackground = (promise, { onError } = {}) => {
    Promise.resolve(promise).catch((err) => {
      console.error('Checkpoint upload failed:', err);
      if (onError) onError(err);
      else toast.error(getBkErrorMessage(err));
    });
  };

  const warnMissingCheckpointContext = (ctx, mesh) => {
    if (!ctx) {
      toast.warning(
        'Order context missing; open the order from BengaitLabs first. Continuing without checkpoint upload.',
      );
    } else if (!mesh) {
      toast.warning('No mesh in the editor; continuing without checkpoint upload.');
    }
  };

  const executeForwardNavigation = useCallback(
    (targetStep) => {
      const route = PATHS[targetStep];
      if (!route) return;
      clearToolStates();
      navigate(route);
    },
    [clearToolStates, navigate],
  );

  const executeBackwardNavigation = useCallback(
    async (targetStep) => {
      if (isRevertingRef.current) return;
      const route = PATHS[targetStep];
      if (!route) return;

      isRevertingRef.current = true;
      // Scoped claim, not `setIsLoading`: the revert calls into the replay engine
      // and navigates, and both clear the overlay on their own schedule — the
      // replay when it finishes a step, ThreeScene's scan-fetch effect when its
      // cleanup fires on the pathname change. Either would drop the overlay while
      // the revert is still working. Released in the `finally` below.
      const editor = useEditorStore.getState();
      const loadingToken = editor.beginLoading('backward-revert', 'Reverting changes…');
      await yieldForPaint();

      try {
        setPendingRevertNavigation(targetStep);
        const reverted = await revertToStep(targetStep);

        // Navigating on a failed revert is worse than not moving: the step would
        // open showing the geometry that was supposed to be discarded, and the
        // scene autosave would then write that geometry into the target step's
        // saved state — turning a failed discard into a corrupted design.
        if (!reverted) {
          setPendingRevertNavigation(null);
          toast.error(
            `Could not restore the design to ${targetStep.replace(/_/g, ' ').toLowerCase()} — ` +
              'nothing was discarded. Reload the page and try again.',
          );
          return;
        }

        // Reverting to before the locking step removes the attachment from the
        // socket, so reset the locking method store — otherwise its card keeps a
        // stale "attachment complete" state that no longer matches the mesh.
        const targetIdx = GLOBAL_STEP_ORDER.indexOf(targetStep);
        const lockingIdx = GLOBAL_STEP_ORDER.indexOf(MAIN_PROCESS_STEPS.LOCKING_MECHANISM);
        if (targetIdx >= 0 && targetIdx < lockingIdx) {
          useLockingSystemStore.getState().reset();
        }

        clearToolStates();
        navigate(route);
      } catch (err) {
        console.error('[useStepNavigation] backward revert failed:', err);
        toast.error('Failed to revert changes. Please try again.');
        setPendingRevertNavigation(null);
      } finally {
        useEditorStore.getState().endLoading(loadingToken);
        isRevertingRef.current = false;
      }
    },
    [clearToolStates, navigate],
  );

  const promptBackwardNavigation = useCallback(
    (requestedStep) => {
      const fromStep = useWorkflowStore.getState().currentStep;
      if (!requestedStep || !PATHS[requestedStep]) return;

      // After export only one save point per stage survives, so a mid-stage target
      // cannot be reconstructed. Pull it back to the stage start and say so.
      const { target: targetStep, clamped } = clampBackwardTarget(fromStep, requestedStep);
      if (!PATHS[targetStep]) return;

      if (!isBackwardStepNavigation(fromStep, targetStep)) {
        executeForwardNavigation(targetStep);
        return;
      }
      // A clamped rewind always confirms: it discards more than the user asked for.
      if (clamped) {
        setPendingBackwardNav({
          targetStep,
          message: stageRewindMessage(targetStep),
        });
        return;
      }
      // Only warn about discarding work when the current step actually has
      // changes; otherwise quietly revert/navigate back.
      if (!stepHasUserChanges(fromStep)) {
        void executeBackwardNavigation(targetStep);
        return;
      }
      setPendingBackwardNav({
        targetStep,
        message: getBackwardDiscardMessage(fromStep, targetStep),
      });
    },
    [executeForwardNavigation, executeBackwardNavigation, setPendingBackwardNav],
  );

  const confirmBackwardNavigation = useCallback(async () => {
    if (!pendingBackwardNav) return;
    const { targetStep } = pendingBackwardNav;
    clearPendingBackwardNav();
    await executeBackwardNavigation(targetStep);
  }, [pendingBackwardNav, clearPendingBackwardNav, executeBackwardNavigation]);

  const cancelBackwardNavigation = useCallback(() => {
    clearPendingBackwardNav();
  }, [clearPendingBackwardNav]);

  // Final step: save the design (upload final STL) and redirect to the orders portal.
  const finalizeDesign = async () => {
    const ctx = loadBkDesignOrderContext();
    if (!ctx) {
      toast.error('Order context missing — please reopen this design from your orders.');
      return;
    }

    const shadedMesh = useEditorStore.getState().sceneRefs?.shadedMesh;
    if (!shadedMesh?.geometry) {
      toast.error('No mesh loaded to export.');
      return;
    }

    if (!(await beginCheckpointUpload())) return;
    try {
      const blob = exportMesh(shadedMesh, { format: EXPORT_FORMATS.STL });
      const stlFile = new File([blob], `bk_design_${ctx.sales_order}.stl`, {
        type: 'application/octet-stream',
      });
      // finalize_bk_design_final_stl_direct_upload deducts credits server-side.
      // Do not call a separate deduct API — that would double-debit.
      const result = await uploadFinalStl(ctx.sales_order, ctx.customer, stlFile);
      notifyBkFinalExportSuccessUi(result);
      // Brief pause so the credits/export toast is visible before redirect.
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // The design is delivered: collapse per-tool/per-step undo material down to
      // one mesh per stage. Awaited so the deletes are issued before the page
      // navigates away, but its failure cannot fail the export.
      await runPostExportPrune();
      // Redirect away — keep the busy state since the page is navigating.
      window.location.href = ORDERS_REDIRECT_URL;
    } catch (err) {
      const status = err?.response?.status ?? err?.frappeStatusCode;
      if (status === 402) {
        toast.error(
          getBkErrorMessage(err) || 'Insufficient BenX credits to complete this export.',
        );
      } else if (status === 409) {
        toast.error(getBkErrorMessage(err) || 'This version already exists. Try again.');
      } else if (status === 401 || status === 403) {
        toast.error('You are not authenticated. Please sign in again.');
      } else {
        toast.error(getBkErrorMessage(err) || 'Export failed. Please try again.');
      }
      endCheckpointUpload();
    }
  };

  const handleNext = async () => {
    if (isCheckpointUploadingRef.current) return;

    // Skippable steps (tools optional): if nothing was applied, confirm the
    // skip first. Steps that require an apply are gated by the disabled Next
    // button instead and never reach here unconfirmed.
    const skipPlan = getSkipDialogPlan(currentStep);
    if (skipPlan.show) {
      setPendingSkip({
        step: currentStep,
        toolNames: skipPlan.toolNames,
      });
      return;
    }

    await performNext();
  };

  const confirmSkip = async () => {
    clearPendingSkip();
    await performNext();
  };

  const cancelSkip = () => {
    clearPendingSkip();
  };

  async function performNext() {
    if (isCheckpointUploadingRef.current) return;

    if (currentStep === MAIN_PROCESS_STEPS.FINALIZATION) {
      await finalizeDesign();
      return;
    }

    // Capture context/mesh synchronously so background uploads use the mesh as
    // it is *now*, even though we navigate away immediately below.
    const ctx = loadBkDesignOrderContext();
    const mesh = useEditorStore.getState().sceneRefs?.shadedMesh;

    if (currentStep === SCAN_PROCESS_STEPS.PREPARING_SCAN) {
      if (ctx) {
        runCheckpointInBackground(
          useWorkflowStore.getState().actions.persistPreparingScanMetadata(),
          { onError: (err) => toast.warning(`Scale not saved: ${getBkErrorMessage(err)}`) },
        );
      } else {
        toast.warning('Order context missing; continuing without saving scale to the server.');
      }
    }

    // Last scan step (the preview screen is gone): persist the scan final-preview
    // checkpoint in the background and continue to clinical modification.
    if (currentStep === SCAN_PROCESS_STEPS.AXES_ALIGNMENT) {
      runCheckpointInBackground(
        useWorkflowStore
          .getState()
          .actions.persistScanFinalPreviewToErpIfNeeded({
            sales_order: ctx?.sales_order,
            customer: ctx?.customer,
          })
          .then((r) => {
            if (r && !r.skipped && !r.ok && r.error) {
              toast.warning(`Could not save scan preview: ${getBkErrorMessage(r.error)}`);
            }
          }),
      );
      useWorkflowStore.getState().actions.setBkMainProcessIntent('fresh');
    }

    // Every other step saves its model + metadata through the shared per-step
    // uploader. AXES_ALIGNMENT is excluded above because it persists through
    // `persistScanFinalPreviewToErpIfNeeded`, which additionally waits for
    // hydration and dedupes across deep links.
    if (currentStep !== SCAN_PROCESS_STEPS.AXES_ALIGNMENT) {
      if (ctx && mesh) {
        runCheckpointInBackground(
          uploadStepCheckpoint(currentStep, mesh, ctx.sales_order, ctx.customer).then((res) => {
            if (res?.skipped) return null;
            return useWorkflowStore
              .getState()
              .actions.fetchScanFiles(ctx.sales_order, ctx.customer);
          }),
          {
            onError: (err) =>
              toast.warning(
                `Could not save ${currentStep} checkpoint: ${getBkErrorMessage(err)}`,
              ),
          },
        );
      } else if (CHECKPOINT_STEPS.includes(currentStep)) {
        warnMissingCheckpointContext(ctx, mesh);
      }
    }

    clearToolStates();
    const nextRoute = completeCurrentStep();
    if (nextRoute) navigate(nextRoute);
  }

  const handlePrevious = () => {
    const { currentWorkflow, currentStep: step } = useWorkflowStore.getState();
    const targetStep = resolvePreviousStep(currentWorkflow, step);
    if (targetStep) {
      promptBackwardNavigation(targetStep);
    }
  };

  const navigateToStep = (targetStep, { isNextStepWithHandler = false } = {}) => {
    if (isNextStepWithHandler) {
      clearToolStates();
      completeCurrentStep();
      return;
    }
    promptBackwardNavigation(targetStep);
  };

  // `VITE_SKIPPABLE_STEPS` lists steps whose mandatory gate is lifted for this
  // build, so Next stays enabled with nothing applied.
  const isGateOverridden = isStepSkipOverridden(currentStep);

  const isAxesAlignmentActive = currentStep === SCAN_PROCESS_STEPS.AXES_ALIGNMENT;
  const isAxesNextBlocked =
    isAxesAlignmentActive && (!areAllSelectionsCompleted() || !isAligned);

  // Steps that cannot be skipped: keep Next disabled until a tool is applied.
  // A step the user already completed earlier (e.g. revisited via Previous)
  // satisfies the gate even though the live apply flag was reset on revert —
  // mandatory steps can only be completed by applying, so completion implies it.
  const isApplyGateBlocked =
    stepRequiresApply(currentStep) &&
    !stepCompletion[currentStep] &&
    ((currentStep === MAIN_PROCESS_STEPS.TRIMLINE && !isTrimlineApplied) ||
      (currentStep === MAIN_PROCESS_STEPS.THICKNESS && !isThicknessApplied));

  const isNextDisabled =
    import.meta.env.VITE_DEV || isGateOverridden
      ? false
      : (isAxesNextBlocked || isApplyGateBlocked);
  const isPrevDisabled = currentStep === SCAN_PROCESS_STEPS.PREPARING_SCAN;
  const isFinalStep = currentStep === MAIN_PROCESS_STEPS.FINALIZATION;

  return {
    handleNext,
    handlePrevious,
    navigateToStep,
    confirmBackwardNavigation,
    cancelBackwardNavigation,
    pendingSkip,
    confirmSkip,
    cancelSkip,
    isNextDisabled,
    isPrevDisabled,
    isCheckpointUploading,
    isFinalStep,
  };
}
